import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { Linking, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAuth } from './AuthContext';
import { notifyUnauthorized, beginRequest, endRequest, trackRequest, fetchWithTimeout } from '../api/client';
import { getEntraConfig } from '../auth/entraConfig';

// fetch pour appels AUTHENTIFIES : sur 401 (token expiré/revoqué), déclenche
// l'intercepteur global (AuthContext) -> session vidée + redirection auth.
// Timeout (fetchWithTimeout) pour ne jamais rester bloqué si le service ne
// répond pas. Alimente aussi l'indicateur de chargement global.
async function authedFetch(input: string, init?: RequestInit): Promise<Response> {
  beginRequest();
  try {
    const res = await fetchWithTimeout(input, init);
    if (res.status === 401) notifyUnauthorized();
    return res;
  } finally {
    endRequest();
  }
}

// --- Panier INVITÉ persistant (web) ---------------------------------------
// Le panier invité doit survivre au rechargement complet de page provoqué par
// le login Entra (window.location.replace), afin d'être fusionné dans la DB
// après authentification. On le stocke donc dans localStorage.
const GUEST_CART_KEY = 'ccjaw.guestCart';
function loadGuestCart(): any[] {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  try {
    const raw = window.localStorage.getItem(GUEST_CART_KEY);
    const v = raw ? JSON.parse(raw) : [];
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}
function saveGuestCart(items: any[]): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
  } catch {
    /* ignore */
  }
}
function clearGuestCart(): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.removeItem(GUEST_CART_KEY);
  } catch {
    /* ignore */
  }
}

export interface MarketplaceMission {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  price: number;
  originalPrice?: number;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  duration: string;
  rating: number;
  reviews: number;
  isOnSale: boolean;
  category: string;
  coverImage?: any;
  badgeImage?: any;
  tags: {
    domain: string;
    technology: string;
    specialization: string;
    certification?: string;
  };
}

export interface ActiveMissionData {
  id: number;
  title: string;
  subtitle?: string;
  description: string;
  progress: number;
  reward: string;
  timeLeft: string;
  difficulty: string;
  remainingActions: number;
  unreadChats: number;
  todoStories: number;
  isPurchased: boolean;
  price?: number;
  coverImage?: any;
  certification?: string;
}

export interface TrialMission {
  id: number;
  title: string;
  subtitle?: string;
  description: string;
  progress: number;
  timeLeft: string;
  difficulty: string;
  remainingActions: number;
  unreadChats: number;
  todoStories: number;
  fullPrice: number;
  originalPrice?: number;
  isOnSale: boolean;
  trialLimitations: string[];
  category: string;
  coverImage?: any;
  certification?: string;
}

export interface CartItem {
  id: number;
  title: string;
  description: string;
  price: number;
  originalPrice?: number;
  duration: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  quantity: number;
  isOnSale: boolean;
}

export interface CheckoutOrder {
  order_id?: number;
  missions?: number[];
  total?: number;
}

export interface CheckoutResult {
  status?: 'completed' | 'pending' | string;
  free?: boolean;
  simulated?: boolean;
  order?: CheckoutOrder;
  checkout_url?: string;
  session_id?: string;
  /** true si une redirection Stripe a été déclenchée (la page va partir). */
  redirected?: boolean;
}

interface MissionsContextType {
  activeMissions: ActiveMissionData[];
  trialMissions: TrialMission[];
  cartItems: CartItem[];
  completedSequenceItems: Set<number>;
  markSequenceItemComplete: (itemId: number) => void;
  startTrial: (missionId: number) => void;
  buyMission: (missionId: number) => Promise<void>;
  cancelTrial: (missionId: number) => void;
  refreshMyStories: () => Promise<void>;
  refreshCart: () => Promise<void>;
  addToCart: (missionId: number) => Promise<void>;
  removeFromCart: (missionId: number) => Promise<void>;
  checkout: () => Promise<CheckoutResult & { needsLogin?: boolean }>;
  isInTrial: (missionId: number) => boolean;
  isInCart: (missionId: number) => boolean;
  isOwned: (missionId: number) => boolean;
  allMarketplaceMissions: MarketplaceMission[];
  marketplaceLoading: boolean;
}

const MissionsContext = createContext<MissionsContextType | undefined>(undefined);

// Base URL de l'API cc-attack : SOURCE UNIQUE = getEntraConfig().apiBaseUrl
// (backend ACA dev par défaut, surchargeable via window.__CCJAW_API_URL__).
// Évite tout fallback localhost (127.0.0.1:8080) en prod web.
const MARKETPLACE_API_URL = getEntraConfig().apiBaseUrl.replace(/\/$/, '');


const COMPLETED_ITEMS_KEY = 'completedSequenceItems';

export function MissionsProvider({ children }: { children: ReactNode }) {
  const { user, getAccessToken } = useAuth();
  const [activeMissions, setActiveMissions] = useState<ActiveMissionData[]>([]);
  const [trialMissions, setTrialMissions] = useState<TrialMission[]>([]);
  // Initialisé depuis le panier invité persistant (survit au reload du login).
  const [cartItems, setCartItems] = useState<CartItem[]>(() => loadGuestCart() as CartItem[]);
  const [completedSequenceItems, setCompletedSequenceItems] = useState<Set<number>>(new Set([1, 2]));
  // Catalogue marketplace : UNIQUEMENT les vraies missions de l'API (DB).
  // PAS de catalogue statique en fallback : les ids en dur (101+) n'existent pas
  // en base -> "buy" -> POST /me/cart/101 -> 404. On affiche donc vide tant que
  // l'API n'a pas répondu (le fetch est résilient : timeout 30s + retry GET).
  const [marketplaceMissions, setMarketplaceMissions] = useState<MarketplaceMission[]>([]);
  // true tant que le 1er fetch du marketplace n'a pas répondu -> l'UI montre un
  // état de CHARGEMENT (pas "aucun résultat") pendant le cold start du backend.
  const [marketplaceLoading, setMarketplaceLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    trackRequest(fetchWithTimeout(`${MARKETPLACE_API_URL}/public/marketplace/stories`))
      .then(res => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then(data => {
        if (!cancelled && Array.isArray(data?.stories)) {
          setMarketplaceMissions(data.stories as MarketplaceMission[]);
        }
      })
      .catch(() => {
        // API injoignable -> marketplace vide (on n'invente pas de missions non achetables).
      })
      .finally(() => {
        if (!cancelled) setMarketplaceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Missions actives + trial de l'utilisateur connecté, depuis /me/stories (DB).
  // is_trial distingue trial vs actif. Vide si non connecté (pas de données statiques).
  // Fonction réutilisable : appelée au montage et après chaque action (buy/try/cancel).
  const refreshMyStories = useCallback(async () => {
    const token = getAccessToken();
    if (!user || !token) return; // pas connecté -> listes vides (chargées au login)
    try {
      const res = await authedFetch(`${MARKETPLACE_API_URL}/me/stories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!Array.isArray(data?.stories)) return;
      const toActive = (s: any): ActiveMissionData => ({
        id: s.mission_id,
        title: s.title,
        subtitle: s.subtitle ?? undefined,
        description: s.description ?? '',
        progress: s.progress ?? 0,
        reward: s.reward ?? '',
        timeLeft: s.status === 'completed' ? 'Completed' : 'En cours',
        difficulty: s.difficulty ?? 'Medium',
        remainingActions: 0,
        unreadChats: 0,
        todoStories: 0,
        isPurchased: true,
        price: s.price ?? undefined,
        coverImage: s.coverImage ?? undefined,
        certification: s.certification ?? undefined,
      });
      const toTrial = (s: any): TrialMission => ({
        id: s.mission_id,
        title: s.title,
        subtitle: s.subtitle ?? undefined,
        description: s.description ?? '',
        progress: s.progress ?? 0,
        timeLeft: 'Trial',
        difficulty: s.difficulty ?? 'Medium',
        remainingActions: 0,
        unreadChats: 0,
        todoStories: 0,
        fullPrice: s.price ?? 0,
        originalPrice: s.original_price ?? undefined,
        isOnSale: !!s.is_on_sale,
        trialLimitations: Array.isArray(s.trial_limitations) ? s.trial_limitations : [],
        category: s.category ?? '',
        coverImage: s.coverImage ?? undefined,
        certification: s.certification ?? undefined,
      });
      setActiveMissions(data.stories.filter((s: any) => !s.is_trial).map(toActive));
      setTrialMissions(data.stories.filter((s: any) => s.is_trial).map(toTrial));
    } catch (_) {
      // /me/stories indisponible -> on garde le contenu statique.
    }
  }, [user, getAccessToken]);

  useEffect(() => {
    void refreshMyStories();
  }, [refreshMyStories]);

  // Récupère le panier de l'utilisateur connecté depuis la DB (GET /me/cart).
  // Réutilisable : appelé au montage (si user) et après add/remove/checkout.
  // En mode invité (non connecté), NE PAS écraser le panier local : on le garde tel quel.
  const refreshCart = useCallback(async () => {
    const token = getAccessToken();
    if (!user || !token) {
      return; // invité : panier local conservé
    }
    try {
      const res = await authedFetch(`${MARKETPLACE_API_URL}/me/cart`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!Array.isArray(data?.items)) return;
      const items: CartItem[] = data.items.map((it: any) => ({
        id: it.id,
        title: it.title,
        description: it.description ?? '',
        price: Number(it.price ?? 0),
        originalPrice: it.originalPrice != null ? Number(it.originalPrice) : undefined,
        duration: it.duration ?? '',
        difficulty: (it.difficulty ?? 'Medium') as CartItem['difficulty'],
        quantity: it.quantity ?? 1,
        isOnSale: !!it.isOnSale,
      }));
      setCartItems(items);
    } catch (_) {
      // /me/cart indisponible -> on garde l'état courant.
    }
  }, [user, getAccessToken]);

  // Persistance du panier INVITÉ : tant que non connecté, on sauvegarde le
  // panier dans localStorage (pour qu'il survive au reload du login).
  useEffect(() => {
    if (!user) saveGuestCart(cartItems);
  }, [cartItems, user]);

  // Garde d'idempotence : on ne fusionne le panier invité qu'une seule fois par session connectée.
  const mergedGuestCartRef = useRef(false);

  // Synchronisation du panier selon l'état de connexion :
  // - invité -> on garde le panier local (refreshCart ne fait rien).
  // - à la connexion -> si un panier invité existe, on POST chaque mission puis refreshCart (fusion DB).
  //   sinon refreshCart() classique.
  useEffect(() => {
    const token = getAccessToken();
    if (!user || !token) {
      // Déconnexion : on autorise une future fusion lors de la prochaine connexion.
      mergedGuestCartRef.current = false;
      return;
    }
    if (mergedGuestCartRef.current) return; // déjà synchronisé pour cette session
    mergedGuestCartRef.current = true;
    // Lu depuis localStorage (robuste au reload du login), pas l'état mémoire.
    const guestItems = loadGuestCart();
    (async () => {
      if (guestItems.length > 0) {
        for (const item of guestItems) {
          try {
            await authedFetch(`${MARKETPLACE_API_URL}/me/cart/${item.id}`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` },
            });
          } catch (_) {
            // ajout indisponible -> ignore, refreshCart reflètera l'état DB
          }
        }
      }
      clearGuestCart(); // panier invité fusionné -> on l'efface
      await refreshCart();
    })();
  }, [user, getAccessToken, refreshCart]);

  // Retour de Stripe Checkout (web).
  // Nouveau flux panier : ?checkout_session=... -> POST /me/cart/checkout/confirm.
  // Ancien flux : ?stripe_session=... -> POST /me/missions/buy/confirm (conservé).
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const checkoutSession = params.get('checkout_session');
    const legacySession = params.get('stripe_session');
    if (!checkoutSession && !legacySession) return;
    const token = getAccessToken();
    (async () => {
      try {
        if (token && checkoutSession) {
          await authedFetch(`${MARKETPLACE_API_URL}/me/cart/checkout/confirm`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ session_id: checkoutSession }),
          });
        } else if (token && legacySession) {
          await authedFetch(`${MARKETPLACE_API_URL}/me/missions/buy/confirm`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ session_id: legacySession }),
          });
        }
        await refreshCart();
        await refreshMyStories();
      } catch (_) {
        // confirmation indisponible -> ignore
      } finally {
        if (typeof window !== 'undefined' && window.history) {
          window.history.replaceState({}, '', '/');
        }
      }
    })();
  }, [refreshCart, refreshMyStories, getAccessToken]);

  // Load persisted completedSequenceItems on mount
  useEffect(() => {
    AsyncStorage.getItem(COMPLETED_ITEMS_KEY).then(stored => {
      if (stored) {
        try {
          const parsed: number[] = JSON.parse(stored);
          setCompletedSequenceItems(new Set(parsed));
        } catch (_) {}
      }
    });
  }, []);

  const markSequenceItemComplete = (itemId: number) => {
    setCompletedSequenceItems(prev => {
      const next = new Set(prev);
      next.add(itemId);
      // Persist asynchronously
      AsyncStorage.setItem(COMPLETED_ITEMS_KEY, JSON.stringify(Array.from(next)));
      return next;
    });
  };

  const allMarketplaceMissions = marketplaceMissions;

  const isInTrial = (missionId: number) =>
    trialMissions.some(m => m.id === missionId);

  const isInCart = (missionId: number) =>
    cartItems.some(m => m.id === missionId);

  // Mission déjà acquise (présente dans les missions actives = Assignment istrial=0).
  // Source DB-driven : activeMissions provient de /me/stories. Restreint à
  // l'utilisateur connecté (en invité, activeMissions = démo statique -> ne rien
  // masquer). Le backend refuse d'ajouter une mission déjà active au panier
  // (already_active) ; on la masque donc du Marketplace côté UI.
  const isOwned = (missionId: number) =>
    !!user && activeMissions.some(m => m.id === missionId);

  // Démarre un trial via l'API (POST /me/missions/{id}/try) puis rafraîchit.
  const startTrial = async (missionId: number) => {
    const token = getAccessToken();
    if (!token) return;
    try {
      await authedFetch(`${MARKETPLACE_API_URL}/me/missions/${missionId}/try`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      await refreshMyStories();
    } catch (_) {
      // action indisponible -> ignore
    }
  };

  // Ajoute une mission au panier.
  // - connecté (user + token) : POST /me/cart/{id} (Bearer) puis refreshCart (DB).
  // - invité : ajoute un CartItem local construit depuis le catalogue marketplace.
  const addToCart = useCallback(async (missionId: number) => {
    const token = getAccessToken();
    if (user && token) {
      try {
        await authedFetch(`${MARKETPLACE_API_URL}/me/cart/${missionId}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        await refreshCart();
      } catch (_) {
        // action indisponible -> ignore
      }
      return;
    }
    // Mode invité : panier local.
    setCartItems(prev => {
      if (prev.some(it => it.id === missionId)) return prev;
      const mission = marketplaceMissions.find(m => m.id === missionId);
      if (!mission) return prev;
      const item: CartItem = {
        id: mission.id,
        title: mission.title,
        description: mission.description,
        price: mission.price,
        originalPrice: mission.originalPrice,
        duration: mission.duration,
        difficulty: mission.difficulty,
        quantity: 1,
        isOnSale: mission.isOnSale,
      };
      return [...prev, item];
    });
  }, [user, getAccessToken, refreshCart, marketplaceMissions]);

  // Retire une mission du panier.
  // - connecté : DELETE /me/cart/{id} puis refreshCart.
  // - invité : filtre le panier local.
  const removeFromCart = useCallback(async (missionId: number) => {
    const token = getAccessToken();
    if (user && token) {
      try {
        await authedFetch(`${MARKETPLACE_API_URL}/me/cart/${missionId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        await refreshCart();
      } catch (_) {
        // action indisponible -> ignore
      }
      return;
    }
    setCartItems(prev => prev.filter(it => it.id !== missionId));
  }, [user, getAccessToken, refreshCart]);

  // Nouveau flux e-commerce : "Buy" = ajouter au panier (pas d'achat direct).
  const buyMission = useCallback(async (missionId: number) => {
    await addToCart(missionId);
  }, [addToCart]);

  // Déclenche le paiement du panier (POST /me/cart/checkout).
  // - checkout_url présent -> redirection Stripe (web: assign, natif: openURL).
  // - sinon (completed/free/simulated) -> rafraîchit panier + missions.
  const checkout = useCallback(async (): Promise<CheckoutResult & { needsLogin?: boolean }> => {
    const token = getAccessToken();
    // Invité : pas d'appel API, on signale qu'une connexion est requise.
    if (!user || !token) return { needsLogin: true };
    let data: CheckoutResult = {};
    try {
      const res = await authedFetch(`${MARKETPLACE_API_URL}/me/cart/checkout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      data = (await res.json().catch(() => ({}))) as CheckoutResult;
    } catch (_) {
      return { status: 'pending' };
    }
    const checkoutUrl = data?.checkout_url;
    if (checkoutUrl) {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.location.assign(checkoutUrl);
      } else {
        await Linking.openURL(checkoutUrl);
      }
      return { ...data, redirected: true };
    }
    // Commande complétée (gratuit / simulé) : missions activées + panier vidé.
    await refreshCart();
    await refreshMyStories();
    return data;
  }, [user, getAccessToken, refreshCart, refreshMyStories]);

  // Annule un trial via l'API (DELETE /me/missions/{id}/trial) puis rafraîchit.
  const cancelTrial = async (missionId: number) => {
    const token = getAccessToken();
    if (!token) return;
    try {
      await authedFetch(`${MARKETPLACE_API_URL}/me/missions/${missionId}/trial`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      await refreshMyStories();
    } catch (_) {
      // action indisponible -> ignore
    }
  };

  return (
    <MissionsContext.Provider
      value={{
        activeMissions,
        trialMissions,
        cartItems,
        completedSequenceItems,
        markSequenceItemComplete,
        startTrial,
        buyMission,
        cancelTrial,
        refreshMyStories,
        refreshCart,
        addToCart,
        removeFromCart,
        checkout,
        isInTrial,
        isInCart,
        isOwned,
        allMarketplaceMissions,
        marketplaceLoading,
      }}
    >
      {children}
    </MissionsContext.Provider>
  );
}

export function useMissions() {
  const ctx = useContext(MissionsContext);
  if (!ctx) throw new Error('useMissions must be used within a MissionsProvider');
  return ctx;
}

